import { useEffect, useRef } from 'react';

import { TimeValueType } from './TimePickerInput';
import { isTimeValueDisabled } from '../utils';

interface UseTimeInputScrollProps {
  values: number[];
  selectedDate: Date | undefined | null;
  valueType: TimeValueType;
  disabledBeforeTime?: Date;
}

export const useTimeInputScroll = ({
  values,
  selectedDate,
  valueType,
  disabledBeforeTime,
}: UseTimeInputScrollProps) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const valueRefs = useRef<(HTMLDivElement | null)[]>([]);
  const hasScrolledRef = useRef(false);

  const attachTimeValueRef = (idx: number) => (el: HTMLDivElement | null) => (valueRefs.current[idx] = el);

  useEffect(() => {
    if (hasScrolledRef.current) return;

    const timeListContainer = scrollContainerRef.current;
    if (!timeListContainer) return;

    const selectedHour = selectedDate ? selectedDate.getHours() : undefined;
    const selectedMinute = selectedDate ? selectedDate.getMinutes() : undefined;
    const selectedTimeValue = valueType === TimeValueType.HOUR ? selectedHour : selectedMinute;

    const selectedIndex = values.findIndex((value) => value === selectedTimeValue);

    let firstEnabledIndex = -1;
    if (disabledBeforeTime) {
      firstEnabledIndex = values.findIndex(
        (value) => !isTimeValueDisabled({ value, valueType, disabledBeforeTime, selectedHour })
      );
    }

    const scrollToIndex =
      selectedIndex !== -1 &&
      !isTimeValueDisabled({ value: values[selectedIndex], valueType, disabledBeforeTime, selectedHour })
        ? selectedIndex
        : firstEnabledIndex;

    if (scrollToIndex !== -1) {
      const timeValueElement = valueRefs.current[scrollToIndex];
      if (timeValueElement) {
        timeValueElement.scrollIntoView({ block: 'center' });
        hasScrolledRef.current = true;
      }
    }
  }, [values, selectedDate, valueType, disabledBeforeTime]);

  return { scrollContainerRef, attachTimeValueRef };
};
